/**
 * QuestionRenderer component for displaying assessment questions
 * 
 * @see src/modules/assessment/models/Question.ts
 */
import React from 'react';
import { Question } from '../../models';
import { useAssessmentContext } from '../../context/AssessmentContext';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { getCategoryDisplayName } from '../../utils/categoryMapping';

interface QuestionRendererProps {
  question: Question;
  onAnswer: (questionId: string, value: number) => void;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({ question, onAnswer }) => {
  const { state } = useAssessmentContext();
  const selectedValue = state.responses[question.id]?.toString() || '';

  const handleValueChange = (value: string) => {
    onAnswer(question.id, parseInt(value, 10));
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 px-4 py-2 rounded-md text-sm text-gray-600">
        Category: {getCategoryDisplayName(question.category)}
      </div>
      
      <h3 className="text-xl font-semibold text-gray-800">{question.text}</h3>
      
      <RadioGroup
        value={selectedValue}
        onValueChange={handleValueChange}
        className="space-y-4"
      >
        {question.options.map((option) => (
          <div key={option.id} className="flex items-start space-x-3 p-3 rounded-md hover:bg-gray-50">
            <RadioGroupItem 
              value={option.score.toString()} 
              id={option.id} 
              className="mt-1"
            />
            <Label 
              htmlFor={option.id} 
              className="font-normal cursor-pointer"
            >
              {option.text}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
};

export default QuestionRenderer;
